import React, { useMemo, useState } from 'react';
import { BarChart3, Trophy, MessageSquare, HardDrive, ShieldCheck, Zap } from 'lucide-react';
import { useLanguage } from '../i18n';
import {
    WITHHELD, compactNumber, formatBytes, formatAverage, moduleRows,
    leaderboardBars, channelRows, dailyTokenBars, metricValue, metricAverage,
    tokenTotals, tokenAxisTicks, tokenBarPct, tokenLabelIndices, shortDayLabel,
    longDayLabel, exactNumber, energyTotals, energyDailyBars, formatPoints,
} from '../utils/telemetryView';
import { PILLAR_COLORS } from '../utils/energyPalette';

// #334 — usage telemetry on the operator dashboard. Every figure comes from
// the aggregate-only `module_usage_daily` roll-up (today recomputed on open) and
// carries no identity or content; since round 3 they are real counts, however
// few people stand behind them. A null (no figure) is shown as "—".

const MODULE_LABELS = {
    finance: ['auxModuleFinance', 'Finance'],
    reminders: ['auxModuleReminders', 'Reminders'],
    notes: ['auxModuleNotes', 'Notes'],
    boards: ['auxModuleBoards', 'Boards'],
    calendar: ['auxModuleCalendar', 'Calendar'],
    files: ['auxModuleFiles', 'File Manager'],
    chat: ['auxModuleChat', 'Messages'],
};

// PAI card chrome (DESIGN_SYSTEM.md): alpha-white card, hairline border,
// 17px/700 card title with a neutral icon. Accents stay on the data.
const Card = ({ icon: Icon, title, testId, children, className = '' }) => (
    <section data-testid={testId} className={`pai-card px-5 py-4 min-w-0 ${className}`}>
        <h3 className="mb-3 flex items-center gap-2 text-[17px] font-bold text-white min-w-0">
            <Icon size={16} className="flex-shrink-0 text-pai-meta" />
            <span className="truncate">{title}</span>
        </h3>
        {children}
    </section>
);

const Empty = ({ children }) => (
    <div className="py-6 text-center text-[13px] text-white/40">{children}</div>
);

const TH = 'py-2 text-[11.5px] font-semibold uppercase tracking-[0.11em] text-pai-meta';
const ROW = 'border-b border-pai-hairline last:border-b-0 text-pai-secondary';

// Series colours for the token chart: blue / amber is a hue pair that stays
// apart for the common colour-vision deficiencies and on dark glass.
const INPUT_BAR = 'bg-pai-blue';
const OUTPUT_BAR = 'bg-pai-amber';

const PLOT_H = 160;

// #334 (PO review) — the daily token chart: a numeric y axis, thinned date
// labels, an input/output legend, period totals and an exact-figure tooltip on
// hover, focus or tap. Geometry and formatting live in utils/telemetryView.js.
const DailyTokenChart = ({ daily }) => {
    const { t, language } = useLanguage();
    const [active, setActive] = useState(-1);
    const bars = useMemo(() => dailyTokenBars(daily), [daily]);
    const totals = useMemo(() => tokenTotals(daily), [daily]);
    const peak = bars.reduce((m, b) => Math.max(m, b.input, b.output), 0);
    const axis = useMemo(() => tokenAxisTicks(peak), [peak]);
    const labelled = useMemo(() => new Set(tokenLabelIndices(bars.length)), [bars.length]);

    const inputLabel = t('auxTelemetryTokenInput', 'Input');
    const outputLabel = t('auxTelemetryTokenOutput', 'Output');
    const cachedLabel = t('auxTelemetryTokenCached', 'Cached');

    if (bars.length === 0) {
        return <Empty>{t('auxTelemetryNoData', 'No data for this period yet.')}</Empty>;
    }

    const num = (n) => exactNumber(n, language);
    const cur = active >= 0 ? bars[active] : null;
    // Tooltip anchored over the active column, flipped to an edge near the ends
    // so it never spills out of the card.
    const centre = cur ? ((active + 0.5) * 100) / bars.length : 0;
    const tipStyle = centre < 22
        ? { left: 0 }
        : centre > 78
            ? { right: 0 }
            : { left: `${centre}%`, transform: 'translateX(-50%)' };

    const dayAria = (b) => t('auxTelemetryTokenDayAria', '{date}: {input} input, {output} output, {cached} cached tokens', {
        date: longDayLabel(b.day, language), input: num(b.input), output: num(b.output), cached: num(b.cached),
    });

    return (
        <div>
            <dl data-testid="telemetry-token-total" className="grid grid-cols-3 gap-3 mb-4" translate="no">
                {[
                    ['input', inputLabel, totals.input, INPUT_BAR],
                    ['output', outputLabel, totals.output, OUTPUT_BAR],
                    ['cached', cachedLabel, totals.cached, null],
                ].map(([key, lbl, value, swatch]) => (
                    <div key={key} data-series={key} className="min-w-0">
                        <dt className="pai-eyebrow flex items-center gap-1.5 truncate">
                            {swatch && <span aria-hidden="true" className={`inline-block w-2 h-2 rounded-[2px] ${swatch}`} />}
                            <span className="truncate">{t('auxTelemetryTokenTotalOf', 'Total {series}', { series: lbl })}</span>
                        </dt>
                        <dd className="mt-1 text-[15px] font-bold text-white tnum truncate" title={num(value)}>{num(value)}</dd>
                    </div>
                ))}
            </dl>

            <div className="flex items-center justify-between gap-3 mb-2 min-w-0">
                <div data-testid="telemetry-token-legend" className="flex items-center gap-4 text-[12.5px] text-pai-secondary">
                    <span data-series="input" className="flex items-center gap-1.5">
                        <span aria-hidden="true" className={`inline-block w-2.5 h-2.5 rounded-[3px] ${INPUT_BAR}`} />
                        {inputLabel}
                    </span>
                    <span data-series="output" className="flex items-center gap-1.5">
                        <span aria-hidden="true" className={`inline-block w-2.5 h-2.5 rounded-[3px] ${OUTPUT_BAR}`} />
                        {outputLabel}
                    </span>
                </div>
                <span className="text-[12px] text-pai-faint truncate">{t('auxTelemetryTokenHint', 'Hover or tap a day for exact figures.')}</span>
            </div>

            <div className="relative flex min-w-0" onMouseLeave={() => setActive(-1)}>
                {/* Y axis */}
                <div
                    data-testid="telemetry-token-axis"
                    aria-label={t('auxTelemetryTokenAxis', 'Tokens')}
                    className="relative flex-shrink-0 w-11"
                    style={{ height: PLOT_H }}
                    translate="no"
                >
                    {axis.ticks.map((tick) => (
                        <span
                            key={tick.value}
                            data-testid="telemetry-token-tick"
                            className="absolute right-2 text-[10.5px] leading-none text-pai-faint tnum"
                            style={{ bottom: `calc(${tick.pct}% - 5px)` }}
                        >
                            {tick.label}
                        </span>
                    ))}
                </div>

                <div className="relative flex-1 min-w-0">
                    <div className="relative" style={{ height: PLOT_H }}>
                        {axis.ticks.map((tick) => (
                            <span
                                key={tick.value}
                                aria-hidden="true"
                                className={`absolute left-0 right-0 border-t ${tick.value === 0 ? 'border-pai' : 'border-pai-hairline'}`}
                                style={{ bottom: `${tick.pct}%` }}
                            />
                        ))}
                        <div className="absolute inset-0 flex items-end">
                            {bars.map((b, i) => {
                                const inH = tokenBarPct(b.input, axis.max);
                                const outH = tokenBarPct(b.output, axis.max);
                                const on = i === active;
                                return (
                                    <button
                                        key={b.day}
                                        type="button"
                                        data-testid="telemetry-token-day"
                                        data-day={b.day}
                                        aria-label={dayAria(b)}
                                        aria-describedby={on ? 'telemetry-token-tooltip' : undefined}
                                        onMouseEnter={() => setActive(i)}
                                        onFocus={() => setActive(i)}
                                        onBlur={() => setActive(-1)}
                                        onClick={() => setActive(i)}
                                        className={`pai-no-hit-slop relative h-full flex-1 min-w-0 flex items-end justify-center gap-px rounded-[3px] transition-colors focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-white/80 ${on ? 'bg-white/[0.06]' : 'hover:bg-white/[0.04]'}`}
                                    >
                                        {/* A non-zero day keeps a 2px stub so a small output never vanishes. */}
                                        <span aria-hidden="true" className={`block w-[38%] max-w-[12px] rounded-t-[2px] ${INPUT_BAR}`} style={{ height: inH > 0 ? `max(2px, ${inH}%)` : 0 }} />
                                        <span aria-hidden="true" className={`block w-[38%] max-w-[12px] rounded-t-[2px] ${OUTPUT_BAR}`} style={{ height: outH > 0 ? `max(2px, ${outH}%)` : 0 }} />
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* X axis — thinned date labels, newest always shown. */}
                    <div aria-hidden="true" className="relative h-5 flex" translate="no">
                        {bars.map((b, i) => (
                            <div key={b.day} className="relative flex-1 min-w-0">
                                {labelled.has(i) && (
                                    <span
                                        data-testid="telemetry-token-label"
                                        className={`absolute top-[6px] text-[10.5px] leading-none whitespace-nowrap tnum ${i === active ? 'text-white' : 'text-pai-faint'} ${
                                            i === 0 && bars.length > 1 ? 'left-0' : i === bars.length - 1 && bars.length > 1 ? 'right-0' : 'left-1/2 -translate-x-1/2'
                                        }`}
                                    >
                                        {shortDayLabel(b.day, language)}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>

                    {cur && (
                        <div
                            id="telemetry-token-tooltip"
                            data-testid="telemetry-token-tooltip"
                            role="tooltip"
                            translate="no"
                            className="pointer-events-none absolute bottom-full mb-2 z-10 w-max max-w-[260px] rounded-row border border-pai bg-pai-modal shadow-modal-sm px-3 py-2.5"
                            style={tipStyle}
                        >
                            <div className="text-[12.5px] font-semibold text-white mb-1.5">{longDayLabel(cur.day, language)}</div>
                            <dl className="grid grid-cols-[auto_auto] gap-x-4 gap-y-1 text-[12.5px]">
                                <dt className="flex items-center gap-1.5 text-pai-meta"><span aria-hidden="true" className={`inline-block w-2 h-2 rounded-[2px] ${INPUT_BAR}`} />{inputLabel}</dt>
                                <dd data-series="input" className="text-right text-white tnum">{num(cur.input)}</dd>
                                <dt className="flex items-center gap-1.5 text-pai-meta"><span aria-hidden="true" className={`inline-block w-2 h-2 rounded-[2px] ${OUTPUT_BAR}`} />{outputLabel}</dt>
                                <dd data-series="output" className="text-right text-white tnum">{num(cur.output)}</dd>
                                <dt className="pl-3.5 text-pai-meta">{cachedLabel}</dt>
                                <dd data-series="cached" className="text-right text-white tnum">{num(cur.cached)}</dd>
                            </dl>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// #334 round 3 — Energy Points consumed per day, platform-wide: one stacked
// column per day in the Energy page's pillar colours, period totals (total +
// the 5 pillars, which add up to it) and an exact-figure tooltip.
const DailyEnergyChart = ({ energy }) => {
    const { t, language } = useLanguage();
    const [active, setActive] = useState(-1);
    const bars = useMemo(() => energyDailyBars(energy), [energy]);
    const totals = useMemo(() => energyTotals(energy), [energy]);
    const peak = bars.reduce((m, b) => Math.max(m, b.total), 0);
    const axis = useMemo(() => tokenAxisTicks(peak), [peak]);
    const labelled = useMemo(() => new Set(tokenLabelIndices(bars.length)), [bars.length]);
    const pillarLabels = {
        listening: t('energyPillar_listening', 'Listening'),
        viewing: t('energyPillar_viewing', 'Viewing'),
        writing: t('energyPillar_writing', 'Writing'),
        reasoning: t('energyPillar_reasoning', 'Reasoning'),
        executing: t('energyPillar_executing', 'Executing'),
    };
    const pts = (n) => formatPoints(n, language);

    if (!energy || !energy.available || bars.length === 0) {
        return <Empty>{t('auxTelemetryNoData', 'No data for this period yet.')}</Empty>;
    }

    const cur = active >= 0 ? bars[active] : null;
    const centre = cur ? ((active + 0.5) * 100) / bars.length : 0;
    const tipStyle = centre < 22
        ? { left: 0 }
        : centre > 78
            ? { right: 0 }
            : { left: `${centre}%`, transform: 'translateX(-50%)' };

    return (
        <div>
            <dl data-testid="telemetry-energy-total" className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-4" translate="no">
                <div data-pillar="total" className="min-w-0">
                    <dt className="pai-eyebrow truncate">{t('auxTelemetryEnergyTotal', 'Total')}</dt>
                    <dd className="mt-1 text-[15px] font-bold text-white tnum truncate" title={pts(totals.total)}>{pts(totals.total)}</dd>
                </div>
                {totals.pillars.map((p) => (
                    <div key={p.pillar} data-pillar={p.pillar} className="min-w-0">
                        <dt className="pai-eyebrow flex items-center gap-1.5 truncate">
                            <span aria-hidden="true" className="inline-block w-2 h-2 rounded-[2px] flex-shrink-0" style={{ backgroundColor: PILLAR_COLORS[p.pillar] }} />
                            <span className="truncate">{pillarLabels[p.pillar]}</span>
                        </dt>
                        <dd className="mt-1 text-[15px] font-bold text-white tnum truncate" title={pts(p.pts)}>{pts(p.pts)}</dd>
                    </div>
                ))}
            </dl>

            <div className="flex justify-end mb-2 min-w-0">
                <span className="text-[12px] text-pai-faint truncate">{t('auxTelemetryEnergyHint', 'Hover or tap a day for the pillar split.')}</span>
            </div>

            <div className="relative flex min-w-0" onMouseLeave={() => setActive(-1)}>
                <div aria-label={t('auxTelemetryEnergyAxis', 'Points')} className="relative flex-shrink-0 w-11" style={{ height: PLOT_H }} translate="no">
                    {axis.ticks.map((tick) => (
                        <span key={tick.value} data-testid="telemetry-energy-tick" className="absolute right-2 text-[10.5px] leading-none text-pai-faint tnum" style={{ bottom: `calc(${tick.pct}% - 5px)` }}>
                            {tick.label}
                        </span>
                    ))}
                </div>
                <div className="relative flex-1 min-w-0">
                    <div className="relative" style={{ height: PLOT_H }}>
                        {axis.ticks.map((tick) => (
                            <span key={tick.value} aria-hidden="true" className={`absolute left-0 right-0 border-t ${tick.value === 0 ? 'border-pai' : 'border-pai-hairline'}`} style={{ bottom: `${tick.pct}%` }} />
                        ))}
                        <div className="absolute inset-0 flex items-end">
                            {bars.map((b, i) => {
                                const on = i === active;
                                const h = tokenBarPct(b.total, axis.max);
                                return (
                                    <button
                                        key={b.day}
                                        type="button"
                                        data-testid="telemetry-energy-day"
                                        data-day={b.day}
                                        aria-label={t('auxTelemetryEnergyDayAria', '{date}: {total} Energy Points', { date: longDayLabel(b.day, language), total: pts(b.total) })}
                                        aria-describedby={on ? 'telemetry-energy-tooltip' : undefined}
                                        onMouseEnter={() => setActive(i)}
                                        onFocus={() => setActive(i)}
                                        onBlur={() => setActive(-1)}
                                        onClick={() => setActive(i)}
                                        className={`pai-no-hit-slop relative h-full flex-1 min-w-0 flex items-end justify-center rounded-[3px] transition-colors focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-white/80 ${on ? 'bg-white/[0.06]' : 'hover:bg-white/[0.04]'}`}
                                    >
                                        <span aria-hidden="true" className="flex flex-col-reverse w-[60%] max-w-[18px] rounded-t-[2px] overflow-hidden" style={{ height: h > 0 ? `max(2px, ${h}%)` : 0 }}>
                                            {b.pillars.filter((p) => p.pts > 0).map((p) => (
                                                <span key={p.pillar} className="block w-full" style={{ height: `${(100 * p.pts) / b.total}%`, backgroundColor: PILLAR_COLORS[p.pillar] }} />
                                            ))}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div aria-hidden="true" className="relative h-5 flex" translate="no">
                        {bars.map((b, i) => (
                            <div key={b.day} className="relative flex-1 min-w-0">
                                {labelled.has(i) && (
                                    <span className={`absolute top-[6px] text-[10.5px] leading-none whitespace-nowrap tnum ${i === active ? 'text-white' : 'text-pai-faint'} ${
                                        i === 0 && bars.length > 1 ? 'left-0' : i === bars.length - 1 && bars.length > 1 ? 'right-0' : 'left-1/2 -translate-x-1/2'
                                    }`}>
                                        {shortDayLabel(b.day, language)}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                    {cur && (
                        <div id="telemetry-energy-tooltip" data-testid="telemetry-energy-tooltip" role="tooltip" translate="no"
                            className="pointer-events-none absolute bottom-full mb-2 z-10 w-max max-w-[260px] rounded-row border border-pai bg-pai-modal shadow-modal-sm px-3 py-2.5"
                            style={tipStyle}>
                            <div className="text-[12.5px] font-semibold text-white mb-1.5">{longDayLabel(cur.day, language)}</div>
                            <dl className="grid grid-cols-[auto_auto] gap-x-4 gap-y-1 text-[12.5px]">
                                <dt className="text-pai-meta">{t('auxTelemetryEnergyTotal', 'Total')}</dt>
                                <dd data-pillar="total" className="text-right text-white tnum font-semibold">{pts(cur.total)}</dd>
                                {cur.pillars.map((p) => (
                                    <React.Fragment key={p.pillar}>
                                        <dt className="flex items-center gap-1.5 text-pai-meta">
                                            <span aria-hidden="true" className="inline-block w-2 h-2 rounded-[2px]" style={{ backgroundColor: PILLAR_COLORS[p.pillar] }} />
                                            {pillarLabels[p.pillar]}
                                        </dt>
                                        <dd data-pillar={p.pillar} className="text-right text-white tnum">{pts(p.pts)}</dd>
                                    </React.Fragment>
                                ))}
                            </dl>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const TelemetrySection = ({ telemetry, daily, energy }) => {
    const { t } = useLanguage();
    // `t` needs literal fallbacks (i18n contract test), so labels are resolved here.
    const labels = {
        auxModuleFinance: t('auxModuleFinance', 'Finance'),
        auxModuleReminders: t('auxModuleReminders', 'Reminders'),
        auxModuleNotes: t('auxModuleNotes', 'Notes'),
        auxModuleBoards: t('auxModuleBoards', 'Boards'),
        auxModuleCalendar: t('auxModuleCalendar', 'Calendar'),
        auxModuleFiles: t('auxModuleFiles', 'File Manager'),
        auxModuleChat: t('auxModuleChat', 'Messages'),
        auxTelemetryExpenses: t('auxTelemetryExpenses', 'Expenses'),
        auxTelemetryIncome: t('auxTelemetryIncome', 'Income'),
        auxTelemetryTickets: t('auxTelemetryTickets', 'Tickets'),
        auxTelemetryMainChat: t('auxTelemetryMainChat', 'Main Chat'),
        auxTelemetryTelegram: t('auxTelemetryTelegram', 'Telegram'),
    };
    const label = (key, fallback) => labels[key] || fallback;
    const modLabel = (m) => (MODULE_LABELS[m] ? label(MODULE_LABELS[m][0], MODULE_LABELS[m][1]) : m);

    if (!telemetry || !telemetry.available) {
        return (
            <div data-testid="telemetry-unavailable" className="mb-6 text-[12.5px] text-pai-meta">
                {t('auxTelemetryUnavailable', 'Module telemetry is not available yet.')}
            </div>
        );
    }

    const bars = leaderboardBars(telemetry);
    const rows = moduleRows(telemetry);
    const channels = channelRows(telemetry);
    const storageAvg = metricAverage(telemetry, 'storage.footprint_bytes');
    const storageTotal = metricValue(telemetry, 'storage.footprint_bytes');

    return (
        <div data-testid="telemetry-section" className="mb-6 space-y-5">
            <div data-testid="telemetry-privacy-note" className="flex items-start gap-2 rounded-row border border-pai-subtle bg-white/[0.03] px-3.5 py-2.5 text-[12.5px] text-pai-meta">
                <ShieldCheck size={14} className="mt-0.5 flex-shrink-0 text-pai-green" />
                <span>{t('auxTelemetryPrivacyNote', 'Aggregate figures only: no identity or content is attached to any number.')}</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <Card icon={Trophy} title={t('auxTelemetryLeaderboard', 'Module leaderboard')} testId="telemetry-leaderboard">
                    {bars.length === 0 ? (
                        <Empty>{t('auxTelemetryNoData', 'No data for this period yet.')}</Empty>
                    ) : (
                        <div className="space-y-2.5">
                            {bars.map((b) => (
                                <div key={b.module} data-testid="telemetry-leaderboard-row" data-module={b.module} className="flex items-center gap-3 min-w-0">
                                    <div className="w-5 text-[12.5px] text-pai-faint tnum">{b.rank}</div>
                                    <div className="w-28 text-[13.5px] font-semibold text-pai-body truncate">{modLabel(b.module)}</div>
                                    <div className="flex-1 h-2 bg-white/[0.06] rounded-full overflow-hidden">
                                        <div className="h-full bg-white/[0.55] rounded-full" style={{ width: `${b.pct}%` }} />
                                    </div>
                                    <div className="w-16 text-right text-[13px] text-white tnum" translate="no">{compactNumber(b.actions)}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </Card>

                <Card icon={BarChart3} title={t('auxTelemetryDailyTokens', 'Daily tokens (input / output)')} testId="telemetry-daily-tokens">
                    <DailyTokenChart daily={daily} />
                </Card>
            </div>

            <Card icon={Zap} title={t('auxTelemetryEnergy', 'Energy Points consumed')} testId="telemetry-energy">
                <DailyEnergyChart energy={energy} />
            </Card>

            <Card icon={BarChart3} title={t('auxTelemetryModules', 'Modules')} testId="telemetry-modules">
                <div className="overflow-x-auto">
                    <table className="w-full text-[13.5px]">
                        <thead>
                            <tr className="border-b border-pai-subtle">
                                <th className={`${TH} text-left pr-4`}>{t('auxTelemetryModule', 'Module')}</th>
                                <th className={`${TH} text-right px-3`}>{t('auxTelemetryCreated', 'Created in period')}</th>
                                <th className={`${TH} text-right px-3`}>{t('auxTelemetryAvgPerUser', 'Avg per user')}</th>
                                <th className={`${TH} text-left pl-3`}>{t('auxTelemetryBreakdown', 'Breakdown')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.module} data-testid="telemetry-module-row" data-module={r.module} className={ROW}>
                                    <td className="py-2.5 pr-4 font-semibold text-pai-body">{modLabel(r.module)}</td>
                                    <td className="text-right py-2.5 px-3 tnum text-white" translate="no">{compactNumber(r.actions)}</td>
                                    <td className="text-right py-2.5 px-3 tnum text-white" translate="no">{formatAverage(r.avgPerUser)}</td>
                                    <td className="py-2.5 pl-3 text-[12.5px] text-pai-meta tnum" translate="no">
                                        {r.extra.map((e) => `${label(e.labelKey, e.label)} ${compactNumber(e.value)}`).join(' · ')}
                                        {r.module === 'boards' && (
                                            <span>{r.extra.length ? ' · ' : ''}{t('auxTelemetryTicketDensity', 'Tickets per board')} {telemetry.ticket_density_per_board == null ? WITHHELD : telemetry.ticket_density_per_board}</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <Card icon={MessageSquare} title={t('auxTelemetryChannels', 'Channels')} testId="telemetry-channels">
                    <table className="w-full text-[13.5px]">
                        <thead>
                            <tr className="border-b border-pai-subtle">
                                <th className={`${TH} text-left pr-4`}>{t('auxTelemetryChannel', 'Channel')}</th>
                                <th className={`${TH} text-right px-3`}>{t('auxTelemetryMessages', 'Messages')}</th>
                                <th className={`${TH} text-right pl-3`}>{t('auxTelemetryLinked', 'Linked accounts')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {channels.map((c) => (
                                <tr key={c.id} data-testid="telemetry-channel-row" data-channel={c.id} className={ROW}>
                                    <td className="py-2.5 pr-4 font-semibold text-pai-body">{label(c.labelKey, c.label)}</td>
                                    <td className="text-right py-2.5 px-3 tnum text-white" translate="no">{compactNumber(c.messagesValue)}</td>
                                    <td className="text-right py-2.5 pl-3 tnum text-white" translate="no">{c.linked ? compactNumber(c.linkedValue) : ''}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card>

                <Card icon={HardDrive} title={t('auxTelemetryStorage', 'Storage')} testId="telemetry-storage">
                    <div className="grid grid-cols-3 gap-4">
                        <div className="min-w-0">
                            <div className="pai-eyebrow truncate">{t('auxTelemetryStorageTotal', 'Total')}</div>
                            <div className="mt-1 text-[22px] font-extrabold tracking-[-0.02em] text-white tnum" translate="no">{formatBytes(storageTotal)}</div>
                        </div>
                        <div className="min-w-0">
                            <div className="pai-eyebrow truncate">{t('auxTelemetryAvgPerUser', 'Avg per user')}</div>
                            <div className="mt-1 text-[22px] font-extrabold tracking-[-0.02em] text-white tnum" translate="no">{formatBytes(storageAvg)}</div>
                        </div>
                        <div className="min-w-0">
                            <div className="pai-eyebrow truncate">{t('auxTelemetryGrowth', 'Growth in period')}</div>
                            <div data-testid="telemetry-storage-growth" className="mt-1 text-[22px] font-extrabold tracking-[-0.02em] text-white tnum" translate="no">
                                {telemetry.storage_growth_pct == null ? WITHHELD : `${telemetry.storage_growth_pct}%`}
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default TelemetrySection;
